/**
 * @file app/tauri/tauriCompat.js
 *
 * Electron → Tauri V2 compatibility shim for the Happy App desktop build.
 *
 * The web/desktop renderer (react-native-web bundle) was written against an
 * Electron preload surface (`window.ipcRenderer`, `window.appStorage`,
 * `window.osInfo`, `window.configuration`, `window.startMQTTService`,
 * `window.getNetworkInterfaces`). Rather than rewrite every call site, this
 * module re-implements that exact surface on top of Tauri V2 APIs + official
 * plugins, so the same web bundle runs under Tauri with zero component changes.
 *
 * Boot contract: call `await initTauriCompat()` BEFORE rendering React (see the
 * web entry `index.web.js`). The synchronous globals are installed immediately;
 * `initTauriCompat()` then warms the persistent store cache so the renderer's
 * synchronous `appStorage.get()` works.
 *
 * Anything not yet bridged logs a clear `[tauriCompat]` warning and resolves
 * safely instead of throwing — the app always boots.
 *
 * Adapted from Happy-POS `src/tauri/electronCompat.js`, with the MQTT bridge
 * rewritten to match this app's object-callback contract (see
 * `app/helpers/OrdersContext.js` → `setupRealtimeTransport`).
 */

import { invoke } from "@tauri-apps/api/core";
import { listen } from "@tauri-apps/api/event";
import { getCurrentWebviewWindow } from "@tauri-apps/api/webviewWindow";
import { getVersion } from "@tauri-apps/api/app";

import { load as loadStore } from "@tauri-apps/plugin-store";
import * as os from "@tauri-apps/plugin-os";
import { relaunch, exit } from "@tauri-apps/plugin-process";
import { fetch as tauriFetch } from "@tauri-apps/plugin-http";
import * as logPlugin from "@tauri-apps/plugin-log";

import { buildCashDrawerMessage } from "./cashDrawer";
import { processPayment as performPayment } from "./payment";
import { renderReceipt } from "./escposRenderer";
import { MQTT_BROKER_URL, resolveMqttBrokerUrl } from "./integrationsConfig";

const getCurrentWindow = () => getCurrentWebviewWindow();
const tag = "[tauriCompat]";
const noop = () => {};

/** True when running inside a Tauri webview. */
export const isTauri = () =>
  typeof window !== "undefined" &&
  (!!window.__TAURI_INTERNALS__ || !!window.__TAURI__);

// ---------------------------------------------------------------------------
// 1. IPC bridge — window.ipcRenderer
// ---------------------------------------------------------------------------

/** channel -> Set<listener> */
const ipcListeners = new Map();

const addListener = (channel, fn) => {
  if (!ipcListeners.has(channel)) ipcListeners.set(channel, new Set());
  ipcListeners.get(channel).add(fn);
};
const removeListener = (channel, fn) => {
  const set = ipcListeners.get(channel);
  if (set) set.delete(fn);
};
/** Dispatch a backend → renderer message to registered listeners. */
const dispatch = (channel, ...args) => {
  const set = ipcListeners.get(channel);
  if (!set) return;
  const event = { sender: window.ipcRenderer, channel };
  set.forEach((fn) => {
    try {
      fn(event, ...args);
    } catch (e) {
      console.error(`${tag} listener for "${channel}" threw`, e);
    }
  });
};

/** Map a printer name string into the object shape the renderer expects. */
const toPrinterObject = (name, isDefault = false) => ({
  name,
  displayName: name,
  description: "",
  status: 0,
  isDefault,
  paperWidthMm: null,
});

/** Async request/response payment channels (renderer send()s, listens on *Response). */
const REQUEST_RESPONSE = {
  processCreditCard: "processCreditCardResponse",
  processSzepCard: "processSzepCardResponse",
  processNovoEndDay: "processNovoEndDayResponse",
  processNovoEndDaySca: "processNovoEndDayResponse",
  processNovoConnectionTest: "processNovoConnectionTestResponse",
};

/** invoke()-style channels (ipcRenderer.invoke / handle). */
async function routeInvoke(channel, args) {
  switch (channel) {
    case "listSerialPorts":
      return invoke("list_serial_ports");
    case "listSystemPrinters":
    case "listUsbPrinters": {
      const names = await invoke("list_system_printers");
      return (Array.isArray(names) ? names : []).map((n) => toPrinterObject(n));
    }
    default:
      return routeSend(channel, args);
  }
}

/** send()-style channels (fire-and-forget or request/response). */
async function routeSend(channel, args) {
  const win = getCurrentWindow();
  try {
    switch (channel) {
      case "setZoomFactor":
        return win.setZoom(Number(args[0]) || 1);
      case "toggle-main-window-fullscreen": {
        const full = await win.isFullscreen();
        return win.setFullscreen(!full);
      }
      case "set-strict-kiosk-mode":
        return win.setFullscreen(!!args[0]);
      case "restart-app":
        return relaunch();
      case "quit-app":
        return exit(0);
      case "shutdown":
        return invoke("system_shutdown");
      case "manageDNSRecord":
        return manageDnsRecord(args[0]).catch((e) => console.error(`${tag} manageDNSRecord failed`, e));

      // ---- thermal / receipt printing -----------------------------------
      case "printReceipt":
      case "printTCP":
      case "printTerminal":
      case "printLabel":
      case "printStatistics":
        return routePrint(channel, args[0]).catch((e) =>
          dispatch("printerError", String(e && e.message ? e.message : e))
        );

      // ---- cash drawer / cash register ----------------------------------
      case "sendCashDrawer":
        return sendCashDrawer(args[0]);

      // ---- generic raw serial write (fiscal day open/close, custom cmds) -
      case "sendSerial": {
        const { path, baudRate = 9600, parity = "none", data } = args[0] || {};
        if (!path) return { ok: false, message: "no serial port" };
        const bytes =
          typeof data === "string"
            ? Array.from(new TextEncoder().encode(data))
            : Array.from(data || []);
        try {
          await invoke("send_to_serial", { path, baudRate: Number(baudRate) || 9600, parity, data: bytes });
          return { ok: true };
        } catch (e) {
          return { ok: false, message: String(e && e.message ? e.message : e) };
        }
      }

      default:
        break;
    }

    if (REQUEST_RESPONSE[channel]) {
      const result = await performPayment(channel, args[0]);
      dispatch(REQUEST_RESPONSE[channel], result);
      return result;
    }

    console.warn(`${tag} unhandled ipc channel "${channel}" — no-op`);
    return undefined;
  } catch (e) {
    console.error(`${tag} channel "${channel}" failed`, e);
    return undefined;
  }
}

const ipcRenderer = {
  invoke: (channel, ...args) => routeInvoke(channel, args),
  send: (channel, ...args) => {
    routeSend(channel, args);
  },
  on: (channel, fn) => {
    addListener(channel, fn);
    return ipcRenderer;
  },
  once: (channel, fn) => {
    const wrapper = (...a) => {
      removeListener(channel, wrapper);
      fn(...a);
    };
    addListener(channel, wrapper);
    return ipcRenderer;
  },
  removeListener: (channel, fn) => {
    removeListener(channel, fn);
    return ipcRenderer;
  },
  removeAllListeners: (channel) => {
    if (channel) ipcListeners.delete(channel);
    else ipcListeners.clear();
    return ipcRenderer;
  },
};

// ---------------------------------------------------------------------------
// 2. Printing / cash drawer
// ---------------------------------------------------------------------------

/**
 * Send a finished ESC/POS byte buffer to the configured target.
 * `target` may be `TCP://host:port`, `USB://printerName`, a bare serial path
 * (`COM3`, `/dev/ttyUSB0`, or a Bluetooth-Classic SPP port), or a bare system
 * printer name (raw spooler / CUPS raw).
 * @param {string} target
 * @param {Uint8Array|number[]} bytes
 * @param {object} [opts] { baudRate, parity }
 */
async function sendEscposBytes(target, bytes, opts = {}) {
  const data = Array.from(bytes || []);
  if (!data.length) return undefined;
  const t = String(target || "").trim();

  if (t.toUpperCase().startsWith("TCP://")) {
    const [host, portStr] = t.slice(6).split(":");
    return invoke("print_escpos_tcp", { host, port: parseInt(portStr, 10) || 9100, data });
  }
  if (t.toUpperCase().startsWith("USB://")) {
    return invoke("print_raw_to_printer", { printer: t.slice(6), data });
  }
  if (t.toUpperCase().startsWith("SERIAL://") || /^COM\d+$/i.test(t) || t.startsWith("/dev/")) {
    const path = t.replace(/^serial:\/\//i, "");
    return invoke("send_to_serial", {
      path,
      baudRate: Number(opts.baudRate) || 9600,
      parity: opts.parity || "none",
      data,
    });
  }
  if (t) {
    // Bare printer name → Windows RAW spooler / CUPS raw.
    return invoke("print_raw_to_printer", { printer: t, data });
  }
  throw new Error("no printer target configured");
}

/**
 * Route a print job. The renderer's job payload may be the order itself or a
 * wrapper `{ order, config, target }`. Receipt jobs are rendered to ESC/POS
 * here (CP852) and sent over TCP / serial / raw spooler. Pre-built byte
 * payloads (`bytes`/`escpos`) are sent directly.
 */
async function routePrint(channel, job) {
  if (!job) return undefined;
  const order = job.order || job.data?.order || job;
  const config = job.config || job.printerConfig || job.printer || {};
  const target =
    job.target ||
    config.target ||
    config.address ||
    (config.ip ? `TCP://${config.ip}:${config.port || 9100}` : "") ||
    config.printer ||
    config.name ||
    config.path ||
    order.printerAddress ||
    "";
  const paperWidthMm = Number(config.paperWidthMm || config.widthMm || job.paperWidthMm) || 80;
  const opts = { baudRate: config.baudRate, parity: config.parity };

  const preBuilt = job.bytes || job.escpos || null;
  if (preBuilt) return sendEscposBytes(target, preBuilt, opts);

  // Every layout currently renders the standard receipt; kitchen/label variants
  // can pass {bytes} pre-built. This keeps a single tested ESC/POS path.
  const bytes = renderReceipt(order, { paperWidthMm });
  return sendEscposBytes(target, bytes, opts);
}

/**
 * Cash-register / cash-drawer send. Builds the fiscal QR command string
 * (SAM4s `PTG|…` or generic `fiscat/AEE|…`) and writes it to the serial port.
 * Falls back to a plain ESC/POS drawer-kick pulse for a dumb cash drawer.
 * @param {object} content { config:{path|port,baudRate,parity,type}, order, grossSummary }
 */
async function sendCashDrawer(content = {}) {
  const config = content.config || content || {};
  const path = config.path || config.port;
  if (!path) {
    console.warn(`${tag} sendCashDrawer: no serial port configured`);
    return { ok: false, skipped: true, message: "cashRegisterPortNotConfigured" };
  }

  const baudRate = Number(config.baudRate || config.baudRade || 9600);
  const parity = config.parity || "even";

  try {
    const message = buildCashDrawerMessage(content);
    let data;
    if (message) {
      data = Array.from(new TextEncoder().encode(message));
    } else if (String(config.type || "").toLowerCase().includes("sam4s")) {
      return { ok: true, skipped: true };
    } else {
      data = [0x1b, 0x70, 0x00, 0x19, 0xfa]; // generic drawer kick
    }
    await invoke("send_to_serial", { path, baudRate, parity, data });
    return { ok: true };
  } catch (e) {
    return { ok: false, message: String(e && e.message ? e.message : e) };
  }
}

// ---------------------------------------------------------------------------
// 3. Cloudflare DNS — via Tauri HTTP plugin (no hardcoded secrets)
// ---------------------------------------------------------------------------

async function manageDnsRecord(payload = {}) {
  const { token, zoneId, recordId, name, content, type = "A" } = payload || {};
  if (!token || !zoneId || !recordId || !name) {
    console.warn(`${tag} manageDNSRecord: missing token/zoneId/recordId/name`);
    return { ok: false, message: "missing Cloudflare parameters" };
  }
  try {
    const url = `https://api.cloudflare.com/client/v4/zones/${zoneId}/dns_records/${recordId}`;
    const response = await tauriFetch(url, {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ type, name, content, ttl: 120, proxied: false }),
    });
    const data = await response.json().catch(() => ({}));
    return { ok: response.ok, message: data };
  } catch (e) {
    return { ok: false, message: String(e && e.message ? e.message : e) };
  }
}

// ---------------------------------------------------------------------------
// 4. Persistent storage — window.appStorage (electron-store replacement)
// ---------------------------------------------------------------------------

let storeHandle = null;
const memCache = Object.create(null);

const appStorage = {
  get: (key) => memCache[key],
  set: (key, data) => {
    if (data === undefined || data === null) {
      delete memCache[key];
      if (storeHandle) storeHandle.delete(key).catch(noop);
    } else {
      memCache[key] = data;
      if (storeHandle) storeHandle.set(key, data).catch(noop);
    }
    if (storeHandle) storeHandle.save().catch(noop);
  },
  delete: (key) => appStorage.set(key, null),
};

// ---------------------------------------------------------------------------
// 5. Config — window.configuration, store-backed
// ---------------------------------------------------------------------------

const CONFIG_KEY = "__happy_config__";
const configuration = {
  load: () => memCache[CONFIG_KEY] || {},
  get: () => memCache[CONFIG_KEY] || {},
  set: (data) => {
    appStorage.set(CONFIG_KEY, data || {});
    return data;
  },
  find: (key, section) => {
    const cfg = memCache[CONFIG_KEY] || {};
    if (section) return cfg[section] ? cfg[section][key] : undefined;
    return cfg[key];
  },
};

// ---------------------------------------------------------------------------
// 6. os / logging shims
// ---------------------------------------------------------------------------

const platformMap = { windows: "win32", macos: "darwin", linux: "linux" };

const buildOsInfo = () => {
  let plt = "win32";
  let rel = "";
  try {
    plt = platformMap[os.platform()] || os.platform();
    rel = os.version();
  } catch (e) {
    /* ignore */
  }
  return {
    platform: () => plt,
    type: () => (plt === "win32" ? "Windows_NT" : plt),
    release: () => rel,
    arch: () => {
      try {
        return os.arch();
      } catch (e) {
        return "x64";
      }
    },
    cpus: () => [],
    totalmem: () => 0,
    freemem: () => 0,
    hostname: () => "",
    networkInterfaces: () => ({}),
  };
};

const logShim = {
  info: (...a) => logPlugin.info(a.join(" ")).catch(noop) || console.info(...a),
  warn: (...a) => logPlugin.warn(a.join(" ")).catch(noop) || console.warn(...a),
  error: (...a) => logPlugin.error(a.join(" ")).catch(noop) || console.error(...a),
  debug: (...a) => logPlugin.debug(a.join(" ")).catch(noop) || console.debug(...a),
};

// ---------------------------------------------------------------------------
// 7. MQTT — browser mqtt over WebSocket, matching OrdersContext's callbacks
//    (port of app/preload.js startMQTTService to the webview).
// ---------------------------------------------------------------------------

/** Resolve the active restaurant id for MQTT topic subscription. */
async function resolveRestaurantId(clientOptions = {}) {
  if (clientOptions.restaurantId) return clientOptions.restaurantId;
  if (memCache.restaurantId) return memCache.restaurantId;
  try {
    const AsyncStorage = (await import("@react-native-async-storage/async-storage")).default;
    const raw = await AsyncStorage.getItem("restaurant");
    const restaurant = raw ? JSON.parse(raw) : null;
    return restaurant?._id || null;
  } catch (e) {
    return null;
  }
}

function installMqtt() {
  window.startMQTTService = (onMessages = {}, clientOptions = {}) => {
    const safeInvoke = (key, ...args) => {
      if (onMessages && typeof onMessages[key] === "function") {
        try {
          onMessages[key](...args);
        } catch (cbErr) {
          console.warn(`${tag} MQTT callback failed (${key})`, cbErr);
        }
      }
    };
    const safeParse = (msg) => {
      try {
        return JSON.parse(msg.toString());
      } catch (_) {
        return null;
      }
    };

    // mqtt is loaded lazily; webpack aliases `mqtt` → its ESM/WebSocket build.
    let client = null;
    import("mqtt")
      .then((mod) => {
        const mqtt = mod.default || mod;
        const rnd = () => Math.floor(Math.random() * (999 - 100 + 1) + 100);
        // Resolve the broker URL at connect time so an HTTPS page upgrades
        // ws:// → wss:// (mixed-content otherwise throws SecurityError) and the
        // socket protocol matches the scheme actually being dialed.
        const brokerUrl = clientOptions.url || resolveMqttBrokerUrl() || MQTT_BROKER_URL;
        const isSecure = /^(wss|mqtts):\/\//i.test(brokerUrl);
        client = mqtt.connect(brokerUrl, {
          clientId: `mqtt_manager_${rnd()}-${rnd()}`,
          resubscribe: true,
          protocol: isSecure ? "wss" : "ws",
          protocolVersion: 3,
          reconnectPeriod: 10000,
          reschedulePings: true,
          ...clientOptions,
        });

        client.on("connect", async (data) => {
          console.info(`${tag} MQTT connected`);
          safeInvoke("connect", data);
          const restaurantId = await resolveRestaurantId(clientOptions);
          if (restaurantId) {
            client.subscribe(`restaurants/${restaurantId}/#/#`);
            client.subscribe(`restaurants/${restaurantId}/#`);
          }
          client.subscribe("developer/messages");
        });
        client.on("disconnect", (e) => safeInvoke("disconnect", e));
        client.on("close", (e) => safeInvoke("disconnect", e));
        client.on("error", (e) => safeInvoke("disconnect", e));
        client.on("end", (e) => safeInvoke("disconnect", e));
        client.on("reconnect", () => console.log(`${tag} MQTT reconnect`));

        client.on("message", (topic, message) => {
          const parsed = safeParse(message);
          if (!parsed) return;
          const meta = { topic, receivedAt: new Date().toISOString(), qos: 1 };
          safeInvoke("onTopicMessage", parsed, meta);
          if (topic.includes("order/create")) safeInvoke("orderCreated", parsed, meta);
          if (topic.includes("order/pay")) safeInvoke("payOrder", parsed, meta);
          if (topic.includes("order/modify")) safeInvoke("modifyOrder", parsed, meta);
          if (topic.includes("order/cancel")) safeInvoke("cancelOrder", parsed, meta);
          if (topic.includes("orders/reload")) safeInvoke("reloadOrders", meta);
          if (topic.includes("status/open")) safeInvoke("changeDayStatus", true, meta);
          if (topic.includes("status/close")) safeInvoke("changeDayStatus", false, meta);
          if (topic.includes("payment/physical/")) safeInvoke("updateTerminalPayment", parsed, meta);
          if (topic.includes("developer/messages")) safeInvoke("showDeveloperMessage", parsed, meta);
        });
      })
      .catch((e) => console.error(`${tag} MQTT init failed`, e));

    const service = {
      publish: (topic, msg) =>
        client && client.publish(topic, typeof msg === "string" ? msg : JSON.stringify(msg)),
      subscribe: (topic) => client && client.subscribe(topic),
      unsubscribe: (topic) => client && client.unsubscribe(topic),
      end: () => client && client.end(),
      get client() {
        return client;
      },
    };
    window.mqttService = service;
    return service;
  };
}

// ---------------------------------------------------------------------------
// 8. Boot
// ---------------------------------------------------------------------------

/**
 * Install all Electron-compatible globals and warm the persistent store cache.
 * Safe to call when NOT under Tauri (it installs no-op-ish shims and returns).
 * @returns {Promise<void>}
 */
export async function initTauriCompat() {
  if (typeof window === "undefined") return;

  window.ipcRenderer = ipcRenderer;
  window.electron = { ipcRenderer };
  window.appStorage = appStorage;
  window.configuration = configuration;
  window.osInfo = buildOsInfo();
  window.electronLog = logShim;
  window.getNetworkInterfaces = async () => {
    try {
      const list = await invoke("get_network_interfaces");
      const arr = Array.isArray(list) ? list : [];
      return arr
        .filter((i) => i && !i.internal && i.family === "IPv4")
        .sort((a, b) => (b.address.startsWith("192.") ? 1 : 0) - (a.address.startsWith("192.") ? 1 : 0));
    } catch (e) {
      console.warn(`${tag} getNetworkInterfaces failed`, e);
      return [];
    }
  };
  installMqtt();

  if (!isTauri()) {
    console.info(`${tag} not running under Tauri — globals installed as no-op shims`);
    return;
  }

  try {
    window.appVersion = await getVersion();
  } catch (e) {
    /* keep injected appVersion */
  }

  try {
    storeHandle = await loadStore("happy-app-storage.json", { autoSave: false });
    const entries = await storeHandle.entries();
    for (const [k, v] of entries) memCache[k] = v;
  } catch (e) {
    console.error(`${tag} store load failed`, e);
  }

  // Stable per-device id (node-machine-id replacement).
  if (!memCache.__device_id__) {
    const uuid =
      (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
      `dev-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
    appStorage.set("__device_id__", uuid);
  }
  window.deviceId = memCache.__device_id__;
  window.machineIdPackage = { machineIdSync: () => window.deviceId };

  try {
    await listen("tauri://update-status", (e) => dispatch("update", e.payload));
  } catch (e) {
    /* updater optional */
  }

  console.info(`${tag} ready — Electron surface emulated on Tauri V2`);
}

export default initTauriCompat;
